// assets/AllCourses/Exercises/DragAndDropExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function DragAndDropExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.dragAndDropQuestions)
    ? exercise.dragAndDropQuestions
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    questions: [],
    hasInteracted: false,
  });

  const completedRef = useRef(false);
useEffect(() => {
  if (!showResults) completedRef.current = false;
}, [showResults]);

  /* ================= INIT STUDENT STATE ================= */
  useEffect(() => {
    if (!isPreview) return;

    const initial = {
      questions: questions.map(q => {
        const blankCount = (q.sentence.match(/___/g) || []).length;
        return {
          availableOptions: [...(q.options || [])].sort(
            () => Math.random() - 0.5
          ),
          droppedAnswers: Array(blankCount).fill(""),
        };
      }),
      hasInteracted: false,
    };

    if (
      exercise.attempt &&
      Array.isArray(exercise.attempt.questions)
    ) {
      setState(exercise.attempt);
    } else {
      setState(initial);
    }
  }, [isPreview, questions, exercise.attempt]);

  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise._id, isPreview]);

  /* ================= EDIT HELPERS ================= */
  const updateQuestions = next => {
    if (isEditMode) onUpdate("dragAndDropQuestions", next);
  };

  const updateQuestionField = (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[qIdx] || {
      sentence: "",
      options: [],
      answer: [],
      requireCapitalization: false,
    };

    let updated = { ...current, [field]: value };

    if (field === "sentence") {
      const blankCount = (value.match(/___/g) || []).length;
      updated.answer = Array.from(
        { length: blankCount },
        (_, i) => current.answer?.[i] || ""
      );
    }

    next[qIdx] = updated;
    updateQuestions(next);
  };

  /* ================= STUDENT DROP ================= */
  const handleDrop = (qIdx, blankIdx, option) => {
    if (!isPreview || showResults || !option) return;

    setState(prev => {
      const next = structuredClone(prev);
      const qState = next.questions[qIdx];
      if (!qState || !qState.availableOptions.includes(option))
        return prev;

      qState.droppedAnswers[blankIdx] = option;
      qState.availableOptions = qState.availableOptions.filter(
        o => o !== option
      );
      next.hasInteracted = true;

      const allFilled = next.questions.every(q =>
        q.droppedAnswers.every(Boolean)
      );

      if (allFilled && !completedRef.current) {
        completedRef.current = true;
        checkAnswers(next);
      }

      return next;
    });
  };

  const checkAnswers = currentState => {
    const allCorrect = questions.every((q, qIdx) => {
      const userAnswers =
        currentState.questions[qIdx]?.droppedAnswers || [];

      return q.answer.every((ans, aIdx) => {
        const user = String(userAnswers[aIdx] || "").trim();
        const correct = String(ans || "").trim();

        return q.requireCapitalization
          ? user === correct
          : user.toLowerCase() === correct.toLowerCase();
      });
    });

    onComplete(exercise._id, allCorrect);
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => {
          const blankCount =
            (q.sentence.match(/___/g) || []).length;

          return (
            <Card key={qIdx} className="p-6">
              <div className="space-y-6">
                <TextareaField
                  label={`Sentence ${qIdx + 1} (use ___ for blanks)`}
                  value={q.sentence || ""}
                  onChange={v =>
                    updateQuestionField(
                      qIdx,
                      "sentence",
                      v
                    )
                  }
                  placeholder="The cat ___ on the ___ every day."
                  rows={4}
                  required
                  error={
                    errors?.[
                      `exercise-${exercise.index}-sentence-${qIdx}`
                    ]
                  }
                />

                <div className="space-y-3">
                  <p className="font-medium">Options</p>
                  {q.options.map((opt, optIdx) => (
                    <div
                      key={optIdx}
                      className="flex gap-3"
                    >
                      <StringField
                        value={opt}
                        onChange={v => {
                          const next = [...questions];
                          next[qIdx].options[optIdx] = v;
                          updateQuestions(next);
                        }}
                        placeholder="Option"
                      />
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          const next = [...questions];
                          next[qIdx].options =
                            next[qIdx].options.filter(
                              (_, i) => i !== optIdx
                            );
                          updateQuestions(next);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const next = [...questions];
                      next[qIdx].options.push("");
                      updateQuestions(next);
                    }}
                  >
                    Add Option
                  </Button>
                </div>

                <div className="space-y-3">
                  <p className="font-medium">
                    Correct Answers
                  </p>
                  {blankCount === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Add <code>___</code> to create blanks
                    </p>
                  ) : (
                    Array.from({ length: blankCount }).map(
                      (_, aIdx) => (
                        <StringField
                          key={aIdx}
                          label={`Blank ${aIdx + 1}`}
                          value={q.answer[aIdx] || ""}
                          onChange={v => {
                            const next = [...questions];
                            next[qIdx].answer[aIdx] = v;
                            updateQuestions(next);
                          }}
                          placeholder="Correct word"
                        />
                      )
                    )
                  )}
                </div>

                <CapitalizationRule
                  checked={!!q.requireCapitalization}
                  onChange={c =>
                    updateQuestionField(
                      qIdx,
                      "requireCapitalization",
                      c
                    )
                  }
                />

                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() =>
                    updateQuestions(
                      questions.filter((_, i) => i !== qIdx)
                    )
                  }
                >
                  Remove Question
                </Button>
              </div>
            </Card>
          );
        })}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              {
                sentence: "",
                options: [],
                answer: [],
                requireCapitalization: false,
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No drag and drop questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">
          Drag and Drop
        </CardTitle>
        <CardDescription>
          Drag the words into the correct blanks.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const parts = q.sentence.split("___");
          const qState =
            state.questions[qIdx] || {
              availableOptions: [],
              droppedAnswers: [],
            };

          return (
            <div key={qIdx} className="space-y-6">
              {/* Word Bank */}
              <div className="rounded-xl border bg-muted/40 p-4">
                <div className="flex flex-wrap justify-center gap-3 min-h-[64px]">
                  {qState.availableOptions.length ? (
                    qState.availableOptions.map(opt => (
                      <Button
                        key={opt}
                        draggable={!showResults}
                        variant="secondary"
                        size="sm"
                        className="cursor-grab"
                        onDragStart={e =>
                          e.dataTransfer.setData(
                            "text/plain",
                            opt
                          )
                        }
                      >
                        {opt}
                      </Button>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      All words used
                    </p>
                  )}
                </div>
              </div>

              {/* Sentence */}
              <div className="rounded-xl border bg-background p-6 text-lg leading-loose flex flex-wrap gap-3">
                {parts.map((part, i) => (
                  <React.Fragment key={i}>
                    <span>{part}</span>
                    {i < parts.length - 1 && (
                      <div
                        onDragOver={e =>
                          e.preventDefault()
                        }
                        onDrop={e =>
                          handleDrop(
                            qIdx,
                            i,
                            e.dataTransfer.getData(
                              "text/plain"
                            )
                          )
                        }
                        className={cn(
                        "inline-flex items-center justify-center min-w-32 h-10 rounded-lg border-2 border-dashed px-3 transition",
                        showResults && "cursor-not-allowed opacity-95",
                          qState.droppedAnswers[i]
                            ? "border-primary bg-primary/10"
                            : "border-muted-foreground/40"
                        )}
                      >
                        {qState.droppedAnswers[i] ? (
                        <div
                          className={cn(
                            "px-3 py-1 rounded transition border font-medium",
                            showResults &&
                              (q.requireCapitalization
                                ? qState.droppedAnswers[i] === q.answer[i]
                                : qState.droppedAnswers[i].toLowerCase() ===
                                  q.answer[i].toLowerCase())
                              ? "bg-green-100 border-green-300 text-green-700"
                              : "bg-red-100 border-red-300 text-red-700"
                          )}
                        >
                          {qState.droppedAnswers[i]}
                        </div>
                      ) : (
                          <span className="text-muted-foreground">
                            Drop here
                          </span>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
